/**
 * Admin — Help & Support queries (SupportRequest table).
 */

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Trash2, RefreshCw, LifeBuoy } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { ExpandableText } from '../../components/ui/expandable-text';
import api from '../../services/api';

export const Route = createFileRoute('/admin/support-requests')({
  component: AdminSupportRequests,
});

interface SupportRequest {
  id: string;
  userId: string | null;
  email: string;
  name: string | null;
  category: string;
  message: string;
  source: string | null;
  status: string;
  createdAt: string;
}

const CATEGORY_TONE: Record<string, string> = {
  billing: 'bg-amber-100 text-amber-700 border-amber-200',
  technical: 'bg-red-100 text-red-700 border-red-200',
  account: 'bg-accent text-accent-foreground border-primary/30',
  feature: 'bg-accent text-accent-foreground border-primary/30',
  other: 'bg-muted text-muted-foreground',
};

function AdminSupportRequests() {
  const [rows, setRows] = useState<SupportRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get('/admin/support-requests');
      setRows(r.data);
    } catch (e) {
      console.error('Failed to load support requests:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this support request?')) return;
    setDeleting(id);
    try {
      await api.delete(`/admin/support-requests/${id}`);
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      console.error('Failed to delete:', e);
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Support</h1>
        <p className="text-muted-foreground">{rows.length} support request{rows.length === 1 ? '' : 's'}</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2"><LifeBuoy className="h-5 w-5" /> Queries</CardTitle>
            <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-2" /> Refresh</Button>
          </div>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No support requests yet</p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>From</TableHead>
                    <TableHead>Topic</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">
                        <div>{r.name || '—'}</div>
                        <a href={`mailto:${r.email}`} className="text-primary hover:underline text-xs">{r.email}</a>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs ${CATEGORY_TONE[r.category] || ''}`}>{r.category}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[360px] align-top">
                        <ExpandableText text={r.message} clampLines={4} />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {new Date(r.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => handleDelete(r.id)}
                          disabled={deleting === r.id}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
